export type SubjectFieldDraftOption = {
	readonly value: string | null;
	readonly label: string;
};

export type SubjectFieldDraftArgs = {
	readonly key?: string | null;
	readonly label?: string;
	readonly nature?: string;
	readonly natureLabel?: string;
	readonly options?: readonly SubjectFieldDraftOption[];
	readonly required?: boolean;
	readonly sentToAi?: boolean;
	readonly archived?: boolean;
};

/**
 * One field of a dossier type, saved or being drafted. `key` is `null` until the server derives it
 * on the first save; an option `value` follows the same rule. `new SubjectFieldDraftView({})` is a
 * blank new field.
 */
class SubjectFieldDraftView {
	readonly key: string | null;
	readonly label: string;
	readonly nature: string;
	readonly natureLabel: string;
	readonly options: readonly SubjectFieldDraftOption[];
	readonly required: boolean;
	readonly sentToAi: boolean;
	readonly archived: boolean;

	constructor(args: SubjectFieldDraftArgs = {}) {
		this.key = args.key ?? null;
		this.label = args.label ?? '';
		this.nature = args.nature ?? 'text';
		this.natureLabel = args.natureLabel ?? '';
		this.options = (args.options ?? []).map((option) =>
			Object.freeze({ value: option.value, label: option.label }),
		);
		this.required = args.required ?? false;
		this.sentToAi = args.sentToAi ?? true;
		this.archived = args.archived ?? false;

		Object.freeze(this);
	}

	/** Returns a new draft with `changes` applied. */
	with(changes: SubjectFieldDraftArgs): SubjectFieldDraftView {
		return new SubjectFieldDraftView({
			key: this.key,
			label: this.label,
			nature: this.nature,
			natureLabel: this.natureLabel,
			options: this.options,
			required: this.required,
			sentToAi: this.sentToAi,
			archived: this.archived,
			...changes,
		});
	}
}

export default SubjectFieldDraftView;
